/**
 * outreachTemplates.js — Email/Text/DM templates from the Cold Outreach Playbook
 *
 * Merge fields: {{first_name}}, {{office}}, {{neighborhood}}, {{listing_address}},
 *               {{portfolio_link}}, {{video_link}}, {{your_name}}, {{phone}}, {{website}},
 *               {{instagram}}
 */

export const TEMPLATES = {
  // ── Generic (legacy / fallback) ──
  email_initial: {
    key: 'email_initial',
    label: 'Initial Introduction',
    channel: 'email',
    cadenceDay: 1,
    subject: 'Quick question about {{listing_address}}',
    body: `Hi, {{first_name}},

I came across your work at {{office}} and wanted to reach out. I run Roto Flight Media — we specialize in drone photography, video tours, and aerial content for real estate listings in the Santa Barbara area.

I noticed {{observation}}

I'd love to show you what we can do. Here's a quick look at some of our recent work: {{portfolio_link}}

Would you be open to a 5-minute call this week to see if there's a fit?`,
  },

  email_followup1: {
    key: 'email_followup1',
    label: 'Follow-Up #1',
    channel: 'email',
    cadenceDay: 5,
    subject: 'Re: Quick question about {{listing_address}}',
    body: `Hi, {{first_name}},

Just bumping this to the top of your inbox. I know you're busy — figured a quick look might be easier than a call.

Here's a 30-second reel from a recent shoot we did in Santa Barbara: {{video_link}}

If you've got a listing coming up, I'd love to chat about how we can make it stand out.`,
  },

  email_free_shoot: {
    key: 'email_free_shoot',
    label: 'Free Shoot Offer',
    channel: 'email',
    cadenceDay: 25,
    subject: 'Complimentary shoot for your next listing?',
    body: `Hi, {{first_name}},

I've been wanting to work with agents at {{office}}, and I think your listings would be a great fit for what we do.

Here's my offer: I'll shoot your next listing at no cost — drone aerials, photos, and a short video walkthrough. No strings attached. If you love it, we can talk about working together going forward.

Interested? Just reply with a listing address and I'll get it scheduled.`,
  },

  email_reengage: {
    key: 'email_reengage',
    label: 'Re-Engagement',
    channel: 'email',
    cadenceDay: 45,
    subject: 'Spring market — making listings stand out',
    body: `Hi, {{first_name}},

With the market heating up in Santa Barbara, I wanted to circle back. We've been doing a lot of work in the area lately and the results have been great for our agent partners.

If you have any listings coming to market soon, I'd love to be your go-to media team. Here's our latest reel: {{video_link}}

Let me know if you'd like to chat.`,
  },

  // ── Text Templates (generic) ──
  text_initial: {
    key: 'text_initial',
    label: 'Initial Text',
    channel: 'text',
    cadenceDay: 1,
    body: `Hi, {{first_name}}, This is {{your_name}} with Roto Flight Media. I do drone photography and video for real estate listings here in SB. Saw your work at {{office}} — really impressive. Would love to show you what aerial content could do for your listings. Mind if I send over a quick portfolio link?`,
  },

  text_followup: {
    key: 'text_followup',
    label: 'Follow-Up Text',
    channel: 'text',
    cadenceDay: 5,
    body: `Hi, {{first_name}}, just following up — I know you're slammed. Here's a quick 20-sec clip from a recent shoot: {{video_link}}. Would love to do something like this for your next listing. Lmk if you'd be interested!`,
  },

  text_free_shoot: {
    key: 'text_free_shoot',
    label: 'Free Shoot Text',
    channel: 'text',
    cadenceDay: 25,
    body: `Hi, {{first_name}}, Quick offer — I'd love to shoot your next listing for free (drone + video). No catch, just want to show you the quality. Got anything coming to market soon?`,
  },

  // ── Instagram DM (generic) ──
  dm_after_engaging: {
    key: 'dm_after_engaging',
    label: 'Instagram DM',
    channel: 'instagram_dm',
    cadenceDay: 3,
    body: `Hi, {{first_name}}, Love your content — especially that recent listing post. I run Roto Flight Media and do drone/video content for agents in the SB area. Would love to collab on a listing sometime. Here's some of our recent work: {{portfolio_link}}. Let me know if you'd ever want to chat!`,
  },

  // ══════════════════════════════════════════════════════════
  // SEGMENT-SPECIFIC TEMPLATES
  // ══════════════════════════════════════════════════════════

  // ── TOP PRODUCER ──────────────────────────────────────────
  tp_email_day1: {
    key: 'tp_email_day1', label: 'Intro — Production Company', channel: 'email', cadenceDay: 1, segment: 'top_producer',
    subject: 'Not another photographer — something different for {{office}}',
    body: `Hi, {{first_name}},

This isn't a photographer pitch. I run Roto Flight Media — we're a production company that produces for TV (including the Emmy-nominated Staycation) and brings that same cinematic level to real estate.

What we do that your current media provider likely doesn't:
• FPV drone tours — cinematic indoor flythrough videos that buyers watch start to finish
• Property films — multi-day productions for luxury listings
• 1-3 business day turnaround on standard projects

Here's a recent FPV tour: {{video_link}}

You're clearly doing well at {{office}} — I'm not here to replace your photographer. I'm here to add a layer your listings don't have yet.

Worth a quick look?`,
  },
  tp_email_day7: {
    key: 'tp_email_day7', label: 'Case Study / Portfolio', channel: 'email', cadenceDay: 7, segment: 'top_producer',
    subject: 'How one agent increased engagement 3x with FPV tours',
    body: `Hi, {{first_name}},

Wanted to share something you might find interesting. One of our agents in the SB market started adding FPV drone tours to their listings and saw their online views nearly triple compared to photo-only listings.

The FPV flythrough format is different from anything else on MLS — buyers actually watch the whole thing because it feels like walking through the home.

Full portfolio: {{portfolio_link}}

If you've got a listing coming up where you want to test something new, I'd love to be the team that helps you stand out.`,
  },
  tp_text_day14: {
    key: 'tp_text_day14', label: 'Follow-Up Text', channel: 'text', cadenceDay: 14, segment: 'top_producer',
    body: `Hi, {{first_name}}, {{your_name}} from Roto Flight Media. Sent you a couple emails recently — no pressure, just wanted to put a face to the name. Check us out on IG @rotoflightmedia if you want to see what we're up to. Happy to chat whenever timing works.`,
  },
  tp_email_day45: {
    key: 'tp_email_day45', label: 'Direct Ask', channel: 'email', cadenceDay: 45, segment: 'top_producer',
    subject: 'One listing — let me show you what we can do',
    body: `Hi, {{first_name}},

I'll keep it short — give us one listing and I'll make sure it's the best-marketed property in your portfolio this quarter.

FPV drone tour + photos + aerial, delivered in 1-3 business days: {{portfolio_link}}

If the timing's not right, no worries at all. I'll be here when it is.`,
  },

  // ── LUXURY ────────────────────────────────────────────────
  lx_email_day1: {
    key: 'lx_email_day1', label: 'Intro — Luxury Film + FPV', channel: 'email', cadenceDay: 1, segment: 'luxury',
    subject: 'Your listings deserve more than photos',
    body: `Hi, {{first_name}},

At the price points you work in, buyers don't just want to see a home — they want to feel it. That's what we do at Roto Flight Media.

We produce:
• FPV drone tours — cinematic indoor flythroughs that show the flow of a home like nothing else
• Social reels — vertical content that performs on Instagram and gets your listings attention
• Signature property films — the same production quality we bring to TV

We also produce for the Emmy-nominated show Staycation, so cinematic storytelling is in our DNA.

Here's a recent luxury FPV tour: {{video_link}}

Would love to be your media team for your next listing.`,
  },
  lx_dm_day3: {
    key: 'lx_dm_day3', label: 'Engage + DM', channel: 'instagram_dm', cadenceDay: 3, segment: 'luxury',
    body: `Hi, {{first_name}}, Incredible listings — that {{neighborhood}} property is stunning. I run @rotoflightmedia, we do FPV drone tours and property films for luxury listings in SB. Would love to create something cinematic for one of your properties. Here's our latest: {{video_link}}`,
  },
  lx_text_day7: {
    key: 'lx_text_day7', label: 'FPV Sample Link', channel: 'text', cadenceDay: 7, segment: 'luxury',
    body: `Hi, {{first_name}}, {{your_name}} from Roto Flight. Wanted to share this FPV tour — this is what's getting the most engagement for our luxury agents right now: {{video_link}}. Could see this working really well for your listings. Let me know if you want to chat.`,
  },
  lx_email_day14: {
    key: 'lx_email_day14', label: 'Portfolio Email', channel: 'email', cadenceDay: 14, segment: 'luxury',
    subject: 'Our work in the Santa Barbara luxury market',
    body: `Hi, {{first_name}},

Wanted to share our portfolio — we've been busy in the SB luxury market and I think you'll see a fit with the kind of properties you represent.

Full portfolio: {{portfolio_link}}

What sets us apart:
• FPV drone tours (we're one of the only companies doing this locally)
• 1-3 day turnaround on standard projects
• Full service — photos, video, drone, Matterport, floor plans, all in one shoot

Let me know if your next listing would be a good fit.`,
  },
  lx_email_day30: {
    key: 'lx_email_day30', label: 'Free Reel at Open House', channel: 'email', cadenceDay: 30, segment: 'luxury',
    subject: 'Free social reel at your next open house?',
    body: `Hi, {{first_name}},

I've got an idea — if you have an open house coming up, I'll swing by and shoot a free 30-second social reel for you. No cost, no commitment.

You'll get a ready-to-post Instagram reel the next morning, and a chance to see our FPV drones in action.

Just send me the address and date, and I'll be there.`,
  },
  lx_email_day45: {
    key: 'lx_email_day45', label: 'Follow-Up', channel: 'email', cadenceDay: 45, segment: 'luxury',
    subject: 'Circling back — Roto Flight Media',
    body: `Hi, {{first_name}},

Just wanted to circle back one more time. We've been doing a lot of luxury work recently and the FPV format is really catching on with buyers and agents alike.

Whenever you have a listing where you want to do something special, I'd love to be the team behind it. No pressure — just keeping the door open.`,
  },

  // ── MID-VOLUME ────────────────────────────────────────────
  mv_email_day1: {
    key: 'mv_email_day1', label: 'Intro — Bundle Value', channel: 'email', cadenceDay: 1, segment: 'mid_volume',
    subject: 'One shoot, everything you need — delivered in 1-3 days',
    body: `Hi, {{first_name}},

I run Roto Flight Media here in Santa Barbara. We do photos, drone, video, FPV tours, Matterport, and floor plans — all in one shoot, delivered in 1-3 business days.

What agents love about working with us:
• One team instead of coordinating 3 different vendors
• Book 2 services together and save 10%, or 3+ for 15% off
• Fast turnaround — most projects delivered in 1-3 business days

Here's a quick sample: {{video_link}}

If you've got a listing coming up, let me quote it for you — no commitment.`,
  },
  mv_dm_day3: {
    key: 'mv_dm_day3', label: 'Engage + DM', channel: 'instagram_dm', cadenceDay: 3, segment: 'mid_volume',
    body: `Hi, {{first_name}}, Love your listings. I'm {{your_name}} with @rotoflightmedia — we do photos, drone, video, and FPV tours for agents in SB. Book 2+ services together for a discount. Would love to work on one of your listings. Check us out: {{portfolio_link}}`,
  },
  mv_text_day7: {
    key: 'mv_text_day7', label: 'Follow-Up Text', channel: 'text', cadenceDay: 7, segment: 'mid_volume',
    body: `Hi, {{first_name}}, {{your_name}} from Roto Flight. Following up on my email — just wanted to share this quick reel: {{video_link}}. We do photo + drone + video all in one shoot, 1-3 day delivery. Lmk if you have anything coming to market!`,
  },
  mv_email_day14: {
    key: 'mv_email_day14', label: 'Bundle Reminder', channel: 'email', cadenceDay: 14, segment: 'mid_volume',
    subject: 'Bundle pricing for your next listing',
    body: `Hi, {{first_name}},

Quick reminder on our bundle pricing:
• Book 2 services → 10% off
• Book 3 or more → 15% off

Popular combo: Photos + Aerial + FPV Social Reel — everything you need for MLS and Instagram in one shoot.

We handle everything so you can focus on selling. Delivered in 1-3 business days.

Want me to put together a quick quote for your next listing?`,
  },
  mv_email_day30: {
    key: 'mv_email_day30', label: 'Free Reel Offer', channel: 'email', cadenceDay: 30, segment: 'mid_volume',
    subject: 'Free social reel at your next open house',
    body: `Hi, {{first_name}},

Here's an easy way to see what we can do — if you have an open house coming up, I'll stop by and shoot a free 30-second social reel for you.

You'll get a polished Instagram reel delivered the next morning. No cost, no strings.

If you like it, we can talk about covering your next listing.`,
  },

  // ── NEW AGENT ─────────────────────────────────────────────
  na_email_day1: {
    key: 'na_email_day1', label: 'Intro — First Listing Special', channel: 'email', cadenceDay: 1, segment: 'new_agent',
    subject: 'Make your first listings stand out from day one',
    body: `Hi, {{first_name}},

Congrats on getting into real estate! The first few listings you put out define how clients see you — and professional media makes a huge difference.

I run Roto Flight Media here in Santa Barbara. We help agents at every level look polished and professional from day one:
• Professional photography starting at $349
• Drone aerials for $299
• Twilight shots for $299

We also do FPV drone tours if you want to really wow your sellers.

Delivered in 1-3 business days. Here's our work: {{portfolio_link}}

When your next listing drops, reach out — I'll make sure it looks incredible.`,
  },
  na_dm_day3: {
    key: 'na_dm_day3', label: 'Engage + DM', channel: 'instagram_dm', cadenceDay: 3, segment: 'new_agent',
    body: `Hi, {{first_name}}, Welcome to the SB real estate scene. I'm {{your_name}} with @rotoflightmedia — we help new agents look pro from day one with photos, drone, and video. Would love to help with your first listing when you're ready. Check out our stuff: {{portfolio_link}}`,
  },
  na_text_day7: {
    key: 'na_text_day7', label: 'Portfolio Link', channel: 'text', cadenceDay: 7, segment: 'new_agent',
    body: `Hi, {{first_name}}, {{your_name}} from Roto Flight. Just wanted to share our portfolio so you know what's possible when you're ready: {{portfolio_link}}. Photos start at $349, aerials at $299. Hit me up when your next listing drops — I'll make it look amazing.`,
  },
  na_email_day21: {
    key: 'na_email_day21', label: 'Standing Offer', channel: 'email', cadenceDay: 21, segment: 'new_agent',
    subject: 'Standing offer for your next listing',
    body: `Hi, {{first_name}},

Just wanted to leave this with you — whenever you have a listing that you want to look its best, reach out. I'll make sure we get you a fast quote and turn it around in 1-3 business days.

No pressure, no timeline. I'm here when you need me.`,
  },
};

export const TEMPLATE_LIST = Object.values(TEMPLATES);

// ── Segment-aware template lookup ───────────────────────────

/**
 * Find the best template for a cadence step + segment.
 * Tries segment-specific first, falls back to generic.
 */
export function getTemplateForStep(cadenceDay, channel, segment) {
  // Try segment-specific
  const segmentMatch = TEMPLATE_LIST.find(t =>
    t.segment === segment && t.cadenceDay === cadenceDay && t.channel === channel
  );
  if (segmentMatch) return segmentMatch;

  // Try segment-specific matching just cadence day (any channel)
  const segmentDayMatch = TEMPLATE_LIST.find(t =>
    t.segment === segment && t.cadenceDay === cadenceDay
  );
  if (segmentDayMatch) return segmentDayMatch;

  // Fall back to generic
  return TEMPLATE_LIST.find(t => !t.segment && t.cadenceDay === cadenceDay && t.channel === channel)
    || TEMPLATE_LIST.find(t => !t.segment && t.channel === channel)
    || null;
}

/**
 * Fill merge fields in a template.
 * @param {string} text — template body or subject
 * @param {object} vars — { first_name, office, ... }
 * @returns {string} — text with filled fields (unfilled fields left as-is)
 */
export function fillTemplate(text, vars = {}) {
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return vars[key] != null && vars[key] !== '' ? vars[key] : match;
  });
}

/**
 * Get merge vars from a prospect contact + settings.
 */
export function getProspectVars(contact, settings = {}) {
  const name = contact.name || '';
  const firstName = name.split(' ')[0] || '';
  const research = contact.data?.ai_research || contact.data?.aiResearch || null;

  // Pull listing/observation from AI research if available
  const recentListing = research?.sales?.recent_listings?.[0];
  const listingAddr = recentListing?.address || '';
  const neighborhood = research?.bio?.market_area || '';
  const observation = research?.outreach?.trigger
    || (recentListing ? `your listing at ${recentListing.address}` : '')
    || (research?.media?.ig_presence ? `your great content on Instagram` : '');

  return {
    first_name: firstName,
    office: contact.company || '',
    listing_address: listingAddr,
    neighborhood: neighborhood,
    observation: observation,
    instagram: contact.data?.ig ? `@${contact.data.ig}` : '@rotoflightmedia',
    portfolio_link: settings.portfolioLink || 'https://rotoflight.com/portfolio',
    video_link: settings.videoLink || 'https://rotoflight.com/reel',
    your_name: settings.yourName || 'Jon',
    phone: settings.companyPhone || '(805) 000-0000',
    website: settings.companyWeb || 'rotoflight.com',
  };
}
